import type { GetStaticPaths, GetStaticProps } from "next";
import type { Page, PageComponent } from "@/services/page";
import { pageService } from "@/services/page/page.service";
import { blogService } from "@/services/page/blog.service";
import { galleryService } from "@/services/page/gallery.service";
import { componentService } from "@/services/page/component.service";
import Head from "next/head";
import LoadingBar from "@/components/ui/LoadingBar";
import HtmlLinksLoader from "@/components/ui/HtmlLinksLoader";
import { Fragment } from "react";
import type { ComponentType } from "react";
import { ComponentData } from "@/types/components";
import ProgressiveComponentLoader from '@/components/ui/ProgressiveComponentLoader'
import dynamic from "next/dynamic";
import { usePageData } from '@/hooks/useDirectus'
import { useRouter } from "next/router";


interface PageProps {
  page: Page;
  preview?: boolean;
  isBot?: boolean;
}

const PROGRESSIVE_ROOT_MARGIN = '100px';
const PROGRESSIVE_FALLBACK_MS = 2000;

// Dynamic component mapping
//Global components
import Header from '@/components/Header/Header'
import Footer from '@/components/Footer/Footer'

//Home components
import HomeHero from '@/components/Home/HomeHero/HomeHero'
import HomeWelcome from '@/components/Home/HomeWelcome/HomeWelcome'
import HomeProcedures from '@/components/Home/HomeProcedures/HomeProcedures'
import HomeBa from '@/components/Home/HomeBa/HomeBa'
import HomeTestimonial from '@/components/Home/HomeTestimonial/HomeTestimonial'
import HomeMeet from '@/components/Home/HomeMeet/HomeMeet'
import HomeVidTest from '@/components/Home/HomeVidTest/HomeVidTest'
import HomeCircleAnimat from '@/components/Home/HomeCircleAnimat/HomeCircleAnimat'
import HomeProc from '@/components/Home/HomeProc/HomeProc'
import HomeReview from '@/components/Home/HomeReview/HomeReview'
import HomeSldOffice from '@/components/Home/HomeSldOffice/HomeSldOffice'
import HomeInstagram from '@/components/Home/HomeInstagram/HomeInstagram'
import HomeContactFooter from '@/components/Home/HomeContactFooter/HomeContactFooter'
import HomeOurOffice from '@/components/Home/HomeOurOffice/HomeOurOffice'
//Secondary components
import SecHero from '@/components/Secondary/SecHero/SecHero'
import Anchors from '@/components/Secondary/Anchors/Anchors'
import SecImgText from '@/components/Secondary/SecImgText/SecImgText'
import SecConditions from '@/components/Secondary/SecConditions/SecConditions'
import SecBa from '@/components/Secondary/SecBa/SecBa'
import SecChoose from '@/components/Secondary/SecChoose/SecChoose'
import SecReview from '@/components/Secondary/SecReview/SecReview'
import SecType from '@/components/Secondary/SecTypes/SecTypes'
import SecOneCol from '@/components/Secondary/SecOneCol/SecOneCol'
import SecTwoCol from '@/components/Secondary/SecTwoCol/SecTwoCol'
import SecProcedures from '@/components/Secondary/SecProcedures/SecProcedures'
import SecRecovery from '@/components/Secondary/SecRecovery/SecRecovery'
import SecBenefits from '@/components/Secondary/SecBenefits/SecBenefits'
import SecCombine from '@/components/Secondary/SecCombine/SecCombine'
import SecReadMoreAbout from '@/components/Secondary/SecReadMoreAbout/SecReadMoreAbout'
import SecFaqs from '@/components/Secondary/SecFaqs/SecFaqs'
import SecBookConsultation from '@/components/Secondary/SecBookConsultation/SecBookConsultation'
import SecAbout from '@/components/Secondary/SecAbout/SecAbout'
import SecBgText from '@/components/Secondary/SecBgText/SecBgText'
import SecYoutubeVideo from '@/components/Secondary/SecYoutubeVideo/SecYoutubeVideo'
//Gallery components
import GalleryIndex from '@/components/Gallery/GalleryIndex/GalleryIndex'
import ProcedureSection from '@/components/Gallery/ProcedureSection/ProcedureSection'
import ProcedureHeader from '@/components/Gallery/ProcedureHeader/ProcedureHeader'
import PatientSection from '@/components/Gallery/PatientSection/PatientSection'
import HeadingSection from '@/components/Gallery/HeadingSection/HeadingSection'
import GalleryBridge from "@/components/Gallery/GalleryBridge/GalleryBridge";
//Blog components
import BlogPostSection from '@/components/Blog/BlogPostSection/BlogPostSection'
import BlogIndexSection from '@/components/Blog/BlogIndexSection/BlogIndexSection'
import BlogCategoryListSection from '@/components/Blog/BlogCategoryListSection/BlogCategoryListSection'
import BlogAllCategoriesSection from '@/components/Blog/BlogAllCategoriesSection/BlogAllCategoriesSection'
import BlogTagListSection from '@/components/Blog/BlogTagListSection/BlogTagListSection'
import BlogAuthorListSection from '@/components/Blog/BlogAuthorListSection/BlogAuthorListSection'
import BlogSearchListSection from '@/components/Blog/BlogSearchListSection/BlogSearchListSection'
import BlogArchiveListSection from '@/components/Blog/BlogArchiveListSection/BlogArchiveListSection'
import BlogHeroMain from '@/components/Blog/BlogHeroMain/BlogHeroMain'
import WrittenBy from '@/components/Secondary/WrittenBy/WrittenBy'
//Testimonials page
import TestimonialsPage from '@/components/TestimonialsPage/TestimonialsPage'
import SecBridge from '@/components/Secondary/SecBridge/SecBridge'
import ContactFormPage from '@/components/ContactForm/ContactFormPage'

type CompWithData = ({ data }: { data: any }) => JSX.Element | null;

const componentMap: Record<string, CompWithData> = {
	//Global components
	header: Header,
	footer: Footer,
	//Home components
	cmp_home_hero: HomeHero,
	cmp_home_welcome: HomeWelcome,
	cmp_home_procedures: HomeProcedures,
	cmp_home_ba: HomeBa,
	cmp_home_testimonial: HomeTestimonial,
	cmp_home_meet: HomeMeet,
	cmp_home_video_testimonials: HomeVidTest,
	cmp_home_logo_animation: HomeCircleAnimat,
	cmp_home_procedures_accordion: HomeProc,
	cmp_home_reviews: HomeReview,
	cmp_home_slider_office: HomeSldOffice,
	cmp_home_instagram: HomeInstagram,
	cmp_home_contact_footer: HomeContactFooter,
	cmp_home_our_office: HomeOurOffice,
	//Secondary components
	cmp_hero_main: SecHero,
	anchors_nav: Anchors,
	cmp_left_image_right_text_split: SecImgText,
	cmp_conditions: SecConditions,
	cmp_before_and_after: SecBa,
	cmp_why_choose_us: SecChoose,
	cmp_testimonials: SecReview,
	cmp_types: SecType,
	cmp_overview_procedures: SecProcedures,
	cmp_one_columns: SecOneCol,
	cmp_two_columns: SecTwoCol,
	cmp_recovery: SecRecovery,
	cmp_benefits: SecBenefits,
	cmp_combine: SecCombine,
	cmp_book_consultation: SecBookConsultation,
	cmp_read_more_about: SecReadMoreAbout,
	cmp_frequently_asked_questions: SecFaqs,
	cmp_about_sec: SecAbout,
	cmp_background_text: SecBgText,
	cmp_youtube_video: SecYoutubeVideo,
	cmp_written_by: WrittenBy,
	cmp_bridge: SecBridge,
	//Gallery components
	gallery_root: GalleryIndex,
	gallery_procedure: ProcedureSection,
	gallery_patient: PatientSection,
	heading_section: HeadingSection,
	gallery_bridge: GalleryBridge,
	//Blog components
	blog_post_section: BlogPostSection,
	blog_index_list_section: BlogIndexSection,
	blog_category_list_section: BlogCategoryListSection,
	blog_all_categories_section: BlogAllCategoriesSection,
	blog_tag_list_section: BlogTagListSection,
	blog_author_list_section: BlogAuthorListSection,
	blog_search_list_section: BlogSearchListSection,
	blog_archive_list_section: BlogArchiveListSection,
	blog_hero_main: BlogHeroMain,

	//Testimonials page
	cmp_testimonials_page: TestimonialsPage,
	cmp_contact_form_page: ContactFormPage,
}

const getSecBgInstanceIndex = (
	components: PageComponentWithKey[],
	index: number
) =>
	components
		.slice(0, index + 1)
		.filter((item) => item.collection === 'cmp_background_text').length

const renderComponentItem = (
	component: PageComponentWithKey,
	componentData: ComponentData,
	index: number,
	components: PageComponentWithKey[] = []
) => {
	const Component = componentMap[component.collection]
	if (!Component) {
		console.warn(`Component not found for collection: ${component.collection}`)
		return null
	}

	const key = component.key ?? `${component.collection}-${index}`
	const secBgInstanceIndex =
		component.collection === 'cmp_background_text'
			? getSecBgInstanceIndex(components, index)
			: undefined

	if (component.item === "root_data") {
		return (
			<Fragment key={key}>
				{componentData[component.collection]?.root_data?.map0 && (
					<h2 className="text-3xl font-bold mb-8 text-center">
						{component.collection}
					</h2>
				)}
				{secBgInstanceIndex !== undefined ? (
					<SecBgText
						data={componentData[component.collection]?.root_data}
						instanceIndex={secBgInstanceIndex}
					/>
				) : (
					<Component data={componentData[component.collection]?.root_data} />
				)}
			</Fragment>
		)
	}

	const data = componentData[component.collection]?.[component.item]
	return (
		<Fragment key={key}>
			{data?.map0 && (
				<h2 className="text-3xl font-bold mb-8 text-left bg-gray-600 text-white p-4">
					{component.collection}
				</h2>
			)}
			{secBgInstanceIndex !== undefined ? (
				<SecBgText data={data} instanceIndex={secBgInstanceIndex} />
			) : (
				<Component data={data} />
			)}
		</Fragment>
	)
}

const renderComponents = (
	components: PageComponentWithKey[] = [],
	componentData: ComponentData
) => {
	return components.map((component, index) =>
		renderComponentItem(component, componentData, index, components)
	)
}

const withKeys = (components: PageComponent[] = [], prefix: string) =>
	components.map((component, index) => ({
		...component,
		key: `${prefix}-${component.collection}-${index}`,
	}))
  
  export default function DynamicPage({ page, isBot }: PageProps) {
	if (!page) return null;
	const { page: swrPage } = usePageData(page.slug || '', page)
	const resolvedPage = swrPage ?? page

	const router = useRouter()
	const isHomeRoute = router.asPath === "/" || router.asPath.startsWith("/?")
	const googleReviewsEnabled =
		router.query.googleReviews === "1" ||
		(Array.isArray(router.query.googleReviews) && router.query.googleReviews[0] === "1")
	const hasHomeReviewsComponent = Boolean(
		resolvedPage.page_components?.some((c: any) => c?.collection === "cmp_home_reviews")
	)
	const shouldInjectHomeReviews = isHomeRoute && googleReviewsEnabled && !hasHomeReviewsComponent
	const pageComponents = resolvedPage.page_components
		? withKeys(resolvedPage.page_components, 'page')
		: []
	
	return (
	  <>
		<LoadingBar />
		<HtmlLinksLoader />
  
		{typeof resolvedPage.template === "object" &&
		  resolvedPage.template?.head_components &&
		  renderComponents(
			resolvedPage.template.head_components.map((component, index) => ({
			  ...component,
			  key: `head-${component.collection}-${index}`
			})),
			resolvedPage.componentData || {}
		  )}
  
		{resolvedPage.page_components ? (
			<ProgressiveComponentLoader
				components={pageComponents}
				renderItem={(component, index) =>
					renderComponentItem(
						component,
						resolvedPage.componentData || {},
						index,
						pageComponents
					)
				}
				initialCount={
					resolvedPage.page_components.length
				}
				rootMargin={PROGRESSIVE_ROOT_MARGIN}
				fallbackMs={PROGRESSIVE_FALLBACK_MS}
			/>
		) : (
			typeof resolvedPage.template === "object" &&
			resolvedPage.template?.main_component && (() => {
				const mainComponents = [
					{
						collection: resolvedPage.template.main_component,
						item: "root_data",
						key: `main-${resolvedPage.template.main_component}`,
					},
				]

				return (
				<ProgressiveComponentLoader
					components={mainComponents}
					renderItem={(component, index) =>
						renderComponentItem(
							component,
							resolvedPage.componentData || {},
							index,
							mainComponents
						)
					}
					initialCount={1}
					rootMargin={PROGRESSIVE_ROOT_MARGIN}
					fallbackMs={PROGRESSIVE_FALLBACK_MS}
				/>
				)
			})()
		)}

		{shouldInjectHomeReviews && (
			<HomeReview
				data={{
					heading: { title: "Google Reviews" },
					content_collection: [],
				}}
			/>
		)}
  
		{typeof resolvedPage.template === "object" &&
		  resolvedPage.template?.foot_components &&
		  renderComponents(
			resolvedPage.template.foot_components.map((component, index) => ({
			  ...component,
			  key: `foot-${component.collection}-${index}`
			})),
			resolvedPage.componentData || {}
		  )}
	  </>
	);
  }
  
  // Add this test data structure
  const testPages = [
		{
			slug: 'test',
			title: 'Home Page',
			description: 'Welcome to our site',
			page_components: [
				{
					collection: 'cmp_testimonials_page',
					item: 'root_data',
					key: 'cmp_testimonials_page',
				},
			],
			template: {
				// head_components: [
				//   {
				//     collection: "header",
				//     item: "header",
				//   },
				// ],
			},
			componentData: {},
			seo: {
				title: 'Test Page',
				description: 'This is a test page for development',
			},
		},
	]
  
  
  
  const REVALIDATE_SECONDS = 60 * 60 * 12; // 12 hours

  export const getStaticPaths: GetStaticPaths = async () => {
	// Keep builds fast; ISR will generate the long tail on demand.
	// Pre-generate "/" and Directus "pages" slugs.
	try {
	  const { readItems } = await import("@directus/sdk");
	  const directus = (await import("@/lib/directus")).default;

	  // Fetch all published page slugs with pagination (Directus may cap responses).
	  const PAGE_SIZE = 500;
	  const slugs: any[] = [];
	  let offset = 0;
	  // eslint-disable-next-line no-constant-condition
	  while (true) {
		const batch = await directus.request<any[]>(
		  readItems("pages", {
			filter: { status: { _eq: "published" } },
			fields: ["slug"],
			sort: ["slug"],
			limit: PAGE_SIZE,
			offset,
		  })
		);

		if (Array.isArray(batch) && batch.length > 0) {
		  slugs.push(...batch);
		}

		if (!Array.isArray(batch) || batch.length < PAGE_SIZE) {
		  break;
		}

		offset += PAGE_SIZE;
	  }

	  const paths: { params: { slug?: string[] } }[] = [
		{ params: { slug: [] } }, // "/"
		...(slugs || [])
		  .map((p) => (p?.slug ? String(p.slug) : null))
		  .filter((slug): slug is string => Boolean(slug))
		  .map((slug) => ({ params: { slug: slug.split("/").filter(Boolean) } })),
	  ];

	  // Warm up common template-based roots too
	  if (process.env.NEXT_PUBLIC_BLOG_SLUG) {
		paths.push({ params: { slug: [process.env.NEXT_PUBLIC_BLOG_SLUG] } });
	  }
	  if (process.env.NEXT_PUBLIC_GALLERY_SLUG) {
		paths.push({ params: { slug: [process.env.NEXT_PUBLIC_GALLERY_SLUG] } });
	  }

	  // Deduplicate (can happen if Directus contains "blog"/"gallery" slugs).
	  const seen = new Set<string>();
	  const uniquePaths = paths.filter((p) => {
		const key = (p.params.slug || []).join("/");
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	  });

	  return { paths: uniquePaths, fallback: "blocking" };
	} catch (e) {
	  console.warn("getStaticPaths failed, falling back to blocking ISR.", e);
	  return { paths: [], fallback: "blocking" };
	}
  };

  export const getStaticProps: GetStaticProps<PageProps> = async (context) => {
	const { params, preview = false } = context;

	const slugParts = (params?.slug as string[]) || [];
	const blogSlug = process.env.NEXT_PUBLIC_BLOG_SLUG || "blog";

	// Skip processing if it's an API route
	if (slugParts[0] === "api") {
	  return { notFound: true, revalidate: REVALIDATE_SECONDS };
	}

	// /blog/search requires query params and is handled by a dedicated SSR route:
	// `src/pages/blog/search.tsx`
	if (
	  slugParts[0] === blogSlug &&
	  (slugParts[1] === "search" || slugParts[1] === process.env.NEXT_PUBLIC_BLOG_SEARCH_SLUG)
	) {
	  return {
		redirect: {
		  destination: `/${blogSlug}`,
		  permanent: false,
		},
		revalidate: REVALIDATE_SECONDS,
	  };
	}

	//for blog index page and pagination
	if (
	  (slugParts[0] === blogSlug && !slugParts[1]) ||
	  (slugParts[0] === blogSlug &&
		slugParts[1] === process.env.NEXT_PUBLIC_BLOG_PAGE_SLUG &&
		slugParts[2])
	) {
	  const currentPage =
		slugParts[1] === process.env.NEXT_PUBLIC_BLOG_PAGE_SLUG
		  ? parseInt(slugParts[2])
		  : 1;

	  // Static generation can't depend on query params; default to newest.
	  const page = await blogService.getBlogPosts(currentPage, 6, "newest");
	  if (page) {
		return {
		  props: { page, preview: preview },
		  revalidate: REVALIDATE_SECONDS,
		};
	  }
	}

	//for blog category index page (all categories)
	if (
	  slugParts[0] === blogSlug &&
	  slugParts[1] === (process.env.NEXT_PUBLIC_BLOG_CATEGORY_SLUG || 'category') &&
	  !slugParts[2]
	) {
	  const page = await blogService.getBlogCategoryIndex();
	  if (page) {
		return {
		  props: { page, preview },
		  revalidate: REVALIDATE_SECONDS,
		};
	  }
	}

	//for blog categories
	if (
	  slugParts[0] === blogSlug &&
	  slugParts[1] === process.env.NEXT_PUBLIC_BLOG_CATEGORY_SLUG &&
	  slugParts[2]
	) {
	  const currentPage =
		slugParts[3] === process.env.NEXT_PUBLIC_BLOG_PAGE_SLUG
		  ? parseInt(slugParts[4])
		  : 1;
	  const categorySlug = slugParts[2];

	  const page = await blogService.getBlogPostsByCategory(categorySlug, currentPage);
	  if (page) {
		return {
		  props: { page, preview: preview },
		  revalidate: REVALIDATE_SECONDS,
		};
	  }
	}

	//for blog tags
	if (
	  slugParts[0] === blogSlug &&
	  slugParts[1] === process.env.NEXT_PUBLIC_BLOG_TAG_SLUG &&
	  slugParts[2]
	) {
	  const currentPage =
		slugParts[3] === process.env.NEXT_PUBLIC_BLOG_PAGE_SLUG
		  ? parseInt(slugParts[4])
		  : 1;
	  const tagSlug = slugParts[2];

	  const page = await blogService.getBlogPostsByTag(tagSlug, currentPage);
	  if (page) {
		return {
		  props: { page, preview: preview },
		  revalidate: REVALIDATE_SECONDS,
		};
	  }
	}

	//for blog authors
	if (
	  slugParts[0] === blogSlug &&
	  slugParts[1] === process.env.NEXT_PUBLIC_BLOG_AUTHOR_SLUG &&
	  slugParts[2]
	) {
	  const currentPage =
		slugParts[3] === process.env.NEXT_PUBLIC_BLOG_PAGE_SLUG
		  ? parseInt(slugParts[4])
		  : 1;
	  const authorSlug = slugParts[2];

	  const page = await blogService.getBlogPostsByAuthor(authorSlug, currentPage);
	  if (page) {
		return {
		  props: { page, preview: preview },
		  revalidate: REVALIDATE_SECONDS,
		};
	  }
	}

	//for blog single post
	if (slugParts[0] === process.env.NEXT_PUBLIC_BLOG_SLUG && slugParts[1]) {
	  const page = await blogService.getBlogPost(slugParts[1]);
	  if (page) {
		return {
		  props: { page, preview: preview },
		  revalidate: REVALIDATE_SECONDS,
		};
	  }
	}

	//for blog archive page
	if (
	  slugParts[0] === blogSlug &&
	  slugParts[1] === process.env.NEXT_PUBLIC_BLOG_ARCHIVE_SLUG &&
	  slugParts[2]
	) {
	  const currentPage =
		slugParts[3] === process.env.NEXT_PUBLIC_BLOG_PAGE_SLUG
		  ? parseInt(slugParts[4])
		  : 1;
	  const archiveSlug = slugParts[2];

	  const page = await blogService.getBlogPostsByArchive(archiveSlug, currentPage);
	  if (page) {
		return {
		  props: { page, preview: preview },
		  revalidate: REVALIDATE_SECONDS,
		};
	  }
	}

	//for gallery index page
	if (slugParts[0] === process.env.NEXT_PUBLIC_GALLERY_SLUG && !slugParts[1]) {
	  const page = await galleryService.getGalleryCategories();
	  if (page) {
		return {
		  props: { page, preview: preview },
		  revalidate: REVALIDATE_SECONDS,
		};
	  }
	}

	//for gallery procedure page
	if (slugParts[0] === process.env.NEXT_PUBLIC_GALLERY_SLUG && slugParts[1] && !slugParts[2]) {
	  const page = await galleryService.getGalleryProcedures(slugParts[1]);
	  if (page) {
		return {
		  props: { page, preview: preview },
		  revalidate: REVALIDATE_SECONDS,
		};
	  }
	}

	//for gallery patient page
	if (
	  slugParts[0] === process.env.NEXT_PUBLIC_GALLERY_SLUG &&
	  slugParts[1] &&
	  slugParts[2] === process.env.NEXT_PUBLIC_GALLERY_PATIENT_SLUG &&
	  slugParts[3]
	) {
	  const page = await galleryService.getGalleryPatients(slugParts[1], slugParts[3]);
	  if (page) {
		return {
		  props: { page, preview: preview },
		  revalidate: REVALIDATE_SECONDS,
		};
	  }
	}

	// For components map page
	if (slugParts[0] === process.env.NEXT_PUBLIC_COMPONENTS_MAP_SLUG && !slugParts[1]) {
	  const page = await componentService.getComponentsMap(componentMap);
	  if (page) {
		return {
		  props: { page, preview: preview },
		  revalidate: REVALIDATE_SECONDS,
		};
	  }
	}

	// For local development, use test data
	if (process.env.ENVIRONMENT === "development") {
	  const slug = !params?.slug
		? "home"
		: Array.isArray(params.slug)
		  ? params.slug.join("/")
		  : params.slug;

	  const page = testPages.find((p) => p.slug === slug);

	  if (page) {
		return {
		  props: { page: page as any, preview: preview },
		  revalidate: 1,
		};
	  }
	}

	// Pages
	const slug = !params?.slug
	  ? "home"
	  : Array.isArray(params.slug)
		? params.slug.join("/")
		: (params.slug as string);

	const page = await pageService.getBySlug(slug);

	if (!page) {
	  return { notFound: true, revalidate: REVALIDATE_SECONDS };
	}

	return {
	  props: { page, preview: preview },
	  revalidate: REVALIDATE_SECONDS,
	};
  };
